import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { AutoNumericDirective } from '../../../directives/auto-numeric.directive';
import { BaseUI } from '../../base.ui';
import { ErrorDialog } from '../../dialog/error/error.dialog';
import { GetMyProductsUseCase, OrderAction, ProductInterface, SelectedProductInterface } from '../../../../core';
import { ParentEvents } from '../../../utils/parent-events.service';
import { Id } from 'src/app/main/domain/data';

@Component({
    selector: 'thr-operate-internal-transfer',
    templateUrl: './operate-internal-transfer.component.html',
    styleUrls: ['./operate-internal-transfer.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class OperateInternalTransferComponent extends BaseUI implements AfterViewInit, OnDestroy, OnInit {
    public OrderAction: typeof OrderAction = OrderAction;

    public initialAmount;
    public initialShares;
    public internalProduct: ProductInterface;
    public internalProducts: ProductInterface[] = [];

    @Input()
    editable = true;

    @Input()
    product: ProductInterface;

    @Input()
    selectedProduct: SelectedProductInterface;

    @ViewChild('sharesDirective')
    sharesDirective: AutoNumericDirective;

    @ViewChild('amountDirective')
    amountDirective: AutoNumericDirective;

    @Input()
    canExonerate = false;

    @Output()
    detail: EventEmitter<Id> = new EventEmitter();

    @Output()
    exonerate: EventEmitter<Id> = new EventEmitter();


    constructor(
        parentEvents: ParentEvents,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        private getMyProductsUseCase: GetMyProductsUseCase
    ) {
        super(parentEvents);
    }

    ngOnInit(): void {
        this.initialShares = this.selectedProduct.order.internalTransferShares || 0;
        this.initialAmount = this.selectedProduct.order.internalTransferAmount || 0;
        this.cdr.detectChanges();

        this.getInternalProducts();
    }

    get minimumInvestment(): number {
        if (this.product) {
            if (this.selectedProduct.amount > 0) {
                return this.product.minimumContribution ? this.product.minimumContribution : 0.01;
            } else {
                return this.product.minimumInvestment ? this.product.minimumInvestment : 0.01;
            }
        }

        return 0;
    }

    get minimumInvestmentMsg(): string {
        if (this.product) {
            if (this.selectedProduct.amount > 0) {
                return 'form.validation.minimum-contribution';
            } else {
                return 'form.validation.minimum-amount';
            }
        }

        return null;
    }

    get maximumInvestment(): number {
        if (this.internalProduct) {
            if (this.selectedProduct.order.action === OrderAction.AMOUNT) {
                return Number((this.internalProduct.amount - this.internalProduct.minimumAmountToMaintain).toFixed(2));
            } else {
                return null;
            }
        }

        return 0;
    }

    get minimumShares(): number {
        if (this.product) {
            const sharePrice = this.internalProduct ? this.internalProduct.amount / this.internalProduct.shares : 0.00000001;

            if (this.selectedProduct.amount > 0) {
                return Number((this.product.minimumContribution / sharePrice).toFixed(8));
            } else {
                return Number((this.product.minimumInvestment / sharePrice).toFixed(8));
            }
        }

        return 0;
    }

    get maximumShares(): number {
        if (this.internalProduct) {
            if (this.selectedProduct.order.action === OrderAction.SHARES) {
                const sharePrice = this.internalProduct ? this.internalProduct.amount / this.internalProduct.shares : 0.00000001;
                const minimumSharesToMaintain = this.internalProduct.minimumAmountToMaintain / sharePrice;
                return Number((this.internalProduct.shares - minimumSharesToMaintain).toFixed(8));
            } else {
                return null;
            }
        }

        return 0;
    }

    private getInternalProducts() {
        this.getMyProductsUseCase.bind().subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.internalProducts = transaction.data.filter((product) => String(product.code) !== String(this.selectedProduct.code));
                this.onTargetProductChange();
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { text: transaction.error },
                });
            }
        });
    }

    onTargetProductChange() {
        this.internalProducts.map((it) => {
            if (String(it.encryptedCode) === this.selectedProduct.order.internalTransferCode) {
                this.internalProduct = it;
                this.cdr.detectChanges();

                if (this.editable) {
                    if (!this.selectedProduct.order.amount || this.selectedProduct.order.amount === 0) {
                        this.selectedProduct.order.action = OrderAction.TOTAL;
                    }
                    this.onActionChange();
                }
            }
        });
    }

    onActionChange() {
        this.selectedProduct.order.internalTransferAmount = 0;
        this.selectedProduct.order.internalTransferShares = 0;
        this.cdr.detectChanges();

        switch (this.selectedProduct.order.action) {
            case OrderAction.TOTAL:
                this.selectedProduct.order.withShares = true;
                this.onTotalChange();
                break;
            case OrderAction.SHARES:
                this.selectedProduct.order.withShares = true;
                this.onSharesChange();
                break;
            case OrderAction.AMOUNT:
                this.selectedProduct.order.withShares = false;
                this.onAmountChange();
                break;
        }

        this.parentEvents.resize();
    }

    onTotalChange() {
        setTimeout(() => {
            this.selectedProduct.order.internalTransferAmount = this.internalProduct.amount;
            this.selectedProduct.order.internalTransferShares = this.internalProduct.shares;

            this.selectedProduct.order.amount = this.internalProduct.amount;
            this.selectedProduct.order.shares = this.product
                ? Number((this.selectedProduct.order.amount / this.product.lastPrice).toFixed(8))
                : 0;

            if (this.sharesDirective) {
                this.sharesDirective.value = this.selectedProduct.order.internalTransferShares;
            }
            if (this.amountDirective) {
                this.amountDirective.value = this.selectedProduct.order.internalTransferAmount;
            }

            this.cdr.detectChanges();
        });
    }

    onSharesChange() {
        setTimeout(() => {
            if (this.sharesDirective) {
                this.selectedProduct.order.internalTransferShares = this.sharesDirective.value;
            }

            const sharePrice = this.internalProduct ? this.internalProduct.amount / this.internalProduct.shares : 0;
            this.selectedProduct.order.internalTransferAmount = Number(
                (this.selectedProduct.order.internalTransferShares * sharePrice).toFixed(2)
            );

            this.selectedProduct.order.amount = this.selectedProduct.order.internalTransferAmount;
            this.selectedProduct.order.shares = this.product
                ? Number((this.selectedProduct.order.amount / this.product.lastPrice).toFixed(8))
                : 0;

            if (this.amountDirective) {
                this.amountDirective.value = this.selectedProduct.order.internalTransferAmount;
            }

            this.cdr.detectChanges();
        });
    }

    onAmountChange() {
        setTimeout(() => {
            if (this.amountDirective) {
                this.selectedProduct.order.internalTransferAmount = this.amountDirective.value;
            }

            const sharePrice = this.internalProduct ? this.internalProduct.amount / this.internalProduct.shares : 0;
            this.selectedProduct.order.internalTransferShares = sharePrice
                ? Number((this.selectedProduct.order.internalTransferAmount / sharePrice).toFixed(8))
                : 0;

            this.selectedProduct.order.amount = this.selectedProduct.order.internalTransferAmount;
            this.selectedProduct.order.shares = this.product
                ? Number((this.selectedProduct.order.amount / this.product.lastPrice).toFixed(8))
                : 0;

            if (this.sharesDirective) {
                this.sharesDirective.value = this.selectedProduct.order.internalTransferShares;
            }

            this.cdr.detectChanges();
        });
    }

    onExonerate() {
        this.exonerate.emit(this.product.id as Id);
    }
}
